import { minutesToClock, minutesToInputTime, type ClassOccurrence, type ISODate } from '@app/shared'

import { DEFAULT_BERKELEY_OFFSET_MINUTES, DEFAULT_END_OFFSET_MINUTES } from './class-labeling'
import type {
  NativeClassPrompt,
  NativeRecorderStatus,
  NativeRecordingEntry,
  NativeSchedule,
  NativeScheduleOccurrence,
  NativeUploadState,
} from './native-recorder'
import { NATIVE_STAGE_PREFIX, type QueueEntry } from './upload-queue'

// ============================================================================
// TEST THE RECORDER — the whole automatic path, at home, in four minutes.
//
// THE OWNER'S WORDS, 2026-09-23: the phone arms class alarms, nobody has ever watched one fire on
// his S24, and he will not gamble a real lecture on it. So this arms ONE made-up class two minutes
// from now, and the screen watches what happens: alarm → prompt → Start tapped → recording starts
// → stops by itself → upload queue → uploaded → transcript. The same alarm, the same prompt, the
// same service, the same queue and the same server as a lecture. Nothing here is a simulation.
//
// ONE TAP, NOT ZERO (2026-09-24). The first run crashed the app at every alarm: Android 14+ will
// not let an alarm open the microphone by itself. The phone now PROMPTS at class time and one tap
// on Start recording starts it (docs/NATIVE-SPIKE.md). The recording still carries
// `scheduled: true` and the alarm's course, so everything below reads it exactly as before.
//
// THREE FACTS OFF THE KOTLIN SIDE DECIDE THE MATH (apps/desktop/plugins/lifeos-recorder):
//   1. `schedule()` is IDEMPOTENT BY REPLACEMENT (ClassSchedule.replaceAll cancels everything it
//      armed last time). So the test occurrence is APPENDED to the week's real occurrences in the
//      same call — sent alone, it would silently disarm every real class. `armingOccurrences`.
//   2. The plugin applies the offsets itself (AlarmMath.berkeley: listed start + 10 for the
//      prompt, listed end + 3 for the stop — two offsets since 2026-10-15). So the occurrence sent
//      is (prompt − start offset) → (stop − end offset), and the plugin adds each back. The real
//      classes' offsets are not touched.
//   3. Times cross as timezone-less wall clock, resolved against America/Los_Angeles
//      (AlarmMath.parseListed). So the instants are written as LA wall clock, seconds included.
//
// WHAT IS "SEEN". Every tick on the checklist is set from a read of something that actually
// happened — the service's status, the phone's own list of files, the durable upload queue, the
// server's lecture list — and it is stored with what was read. Nothing is inferred from the clock:
// "the prompt was due at 3:17 and it is 3:18" is not "the recording started". The prompt's ANSWER
// is read off the phone's own prompt log (`list().prompts`); how Android drew it — full screen or
// a banner — no read can see, and the screen says so rather than guessing.
// ============================================================================

/**
 * The course code the alarm carries. It shows in the notification and slugs into the file name
 * (`…-recorder-test.m4a`), and no Berkeley course code can collide with it: they are a department
 * and a number.
 */
export const RECORDER_TEST_COURSE = 'RECORDER TEST'

/**
 * What the SERVER is told the recording is called — and it is sent UNFILED.
 *
 * The Worker stores `courseCode` verbatim (apps/worker/src/routes/audio.ts: `course_code:
 * record.courseCode`), so sending 'RECORDER TEST' would file a lecture under a course that does
 * not exist. Null is the honest value: the owner can ignore it or file it from its own page.
 */
export const RECORDER_TEST_TITLE = 'Recorder test'

/** One namespaced key, like the journal preferences and the theme. */
export const RECORDER_TEST_STORAGE_KEY = 'lifeos.recorder.test'

/** Where the plugin resolves wall-clock times. AlarmMath.DEFAULT_ZONE, verbatim. */
export const RECORDER_TEST_ZONE = 'America/Los_Angeles'

const MINUTE_MS = 60_000
/** How far ahead the prompt is armed — at least this, rounded up to a whole minute. */
export const RECORDER_TEST_LEAD_MS = 2 * MINUTE_MS
/** How long the test recording runs. */
export const RECORDER_TEST_LENGTH_MS = 2 * MINUTE_MS
/** After the stop, how long the test stays armed — then it drops out of the phone's alarms. */
export const RECORDER_TEST_GRACE_MS = 30 * MINUTE_MS
/** A result nobody dismissed is cleared after a week, so a forgotten card cannot live forever. */
export const RECORDER_TEST_RETENTION_MS = 7 * 24 * 60 * MINUTE_MS
/** A recording that started more than this before the test was armed is not the test's. */
const START_TOLERANCE_MS = MINUTE_MS

// ── the wall clock ──────────────────────────────────────────────────────────

const PARTS = new Map<string, Intl.DateTimeFormat>()

function formatter(zone: string): Intl.DateTimeFormat {
  let found = PARTS.get(zone)
  if (!found) {
    found = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    PARTS.set(zone, found)
  }
  return found
}

/**
 * An instant as the zone's wall clock: `2026-09-23T15:07:00`, no offset — exactly the shape the
 * screen already sends for real classes and the one `AlarmMath.parseListed` reads as local time.
 */
export function wallClock(ms: number, zone: string = RECORDER_TEST_ZONE): string {
  const parts: Record<string, string> = {}
  for (const part of formatter(zone).formatToParts(new Date(ms))) parts[part.type] = part.value
  // Some engines still print midnight as '24' under h23. It is the same instant as '00'.
  const hour = parts.hour === '24' ? '00' : parts.hour
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}`
}

/** The zone's calendar day for an instant. */
export function wallDate(ms: number, zone: string = RECORDER_TEST_ZONE): ISODate {
  return wallClock(ms, zone).slice(0, 10)
}

/** '3:17 PM' for an instant, through the app's one 12-hour formatter. */
export function wallTime(ms: number, zone: string = RECORDER_TEST_ZONE): string {
  const text = wallClock(ms, zone)
  return minutesToClock(Number(text.slice(11, 13)) * 60 + Number(text.slice(14, 16)))
}

// ── the plan ────────────────────────────────────────────────────────────────

export interface RecorderTestPlan {
  /** When the phone should prompt. One tap on Start then starts the recording. A whole minute. */
  promptAtMs: number
  /** When it should stop by itself. */
  stopAtMs: number
  offsetMinutes: number
  /** The end offset taken off the stop, for the plugin to add back. */
  endOffsetMinutes: number
  /** Exactly what goes to `schedule()`, both offsets taken OFF so the plugin can add them back. */
  occurrence: NativeScheduleOccurrence
}

/**
 * The synthetic class. Its REAL start is at least two minutes away (rounded up to the minute, so
 * the time the screen prints is the time the alarm is set for); its LISTED start — what the plugin
 * is handed — is that minus Berkeley Time, and its LISTED end is the stop minus the end offset.
 */
export function planRecorderTest(
  nowMs: number,
  offsetMinutes: number = DEFAULT_BERKELEY_OFFSET_MINUTES,
  endOffsetMinutes: number = DEFAULT_END_OFFSET_MINUTES,
): RecorderTestPlan {
  const promptAtMs = Math.ceil((nowMs + RECORDER_TEST_LEAD_MS) / MINUTE_MS) * MINUTE_MS
  const stopAtMs = promptAtMs + RECORDER_TEST_LENGTH_MS
  return {
    promptAtMs,
    stopAtMs,
    offsetMinutes,
    endOffsetMinutes,
    occurrence: {
      courseCode: RECORDER_TEST_COURSE,
      startsAt: wallClock(promptAtMs - offsetMinutes * MINUTE_MS),
      endsAt: wallClock(stopAtMs - endOffsetMinutes * MINUTE_MS),
      location: null,
    },
  }
}

// ── the stored test ─────────────────────────────────────────────────────────

/** What the screen has actually read, each with when. Absent = not seen yet. */
export interface RecorderTestSeen {
  /** The phone's own read-back of the armed alarm — its instants, Berkeley Time inside. */
  armed?: { startsAt: string; endsAt: string; exact: boolean; seenAt: string }
  /** The phone's own record of how the prompt was answered. Absent while it is still open. */
  prompt?: { outcome: NativeClassPrompt['outcome']; answeredAt: string | null; reason: string | null; seenAt: string }
  /** The service's own start time for the recording. */
  started?: { startedAt: string; seenAt: string }
  /** The file the phone wrote. The identity everything downstream is matched on. */
  path?: string
  /** Read off the phone's list once the file was no longer being written. */
  stopped?: { durationMs: number | null; finalized: boolean; seenAt: string }
  /**
   * The queue entry's own creation time — or, `by: 'phone'`, when the phone's own uploader took
   * the file (its upload state's own timestamp).
   */
  queued?: { at: string; by?: 'phone' }
  /** The server's receipt, as the queue stored it — or as the phone's uploader recorded it. */
  uploaded?: { at: string; status: string; by?: 'phone' }
  /** The phone's own uploader's latest word on the file, while it owns it. */
  native?: { state: NativeUploadState['state']; attempts: number; lastError: string | null; seenAt: string }
  lectureId?: string
  transcribed?: { seenAt: string }
}

export interface RecorderTest {
  armedAt: string
  promptAt: string
  stopAt: string
  offsetMinutes: number
  occurrence: NativeScheduleOccurrence
  seen: RecorderTestSeen
}

export function newRecorderTest(nowMs: number, plan: RecorderTestPlan): RecorderTest {
  return {
    armedAt: new Date(nowMs).toISOString(),
    promptAt: new Date(plan.promptAtMs).toISOString(),
    stopAt: new Date(plan.stopAtMs).toISOString(),
    offsetMinutes: plan.offsetMinutes,
    occurrence: plan.occurrence,
    seen: {},
  }
}

/** Still in the phone's alarms? Until the stop plus half an hour, then it drops out by itself. */
export function recorderTestArming(test: RecorderTest | null, nowMs: number): boolean {
  if (!test) return false
  return nowMs <= Date.parse(test.stopAt) + RECORDER_TEST_GRACE_MS
}

/**
 * THE LIST `schedule()` IS HANDED: the week's real classes, and the test after them while it is
 * armed. Never the test alone — see fact 1 at the top.
 */
export function armingOccurrences(
  days: readonly { occurrences: readonly ClassOccurrence[] }[],
  test: RecorderTest | null,
  nowMs: number,
): NativeScheduleOccurrence[] {
  const real: NativeScheduleOccurrence[] = days.flatMap((day) =>
    day.occurrences.map((occurrence) => ({
      courseCode: occurrence.courseCode,
      startsAt: `${occurrence.date}T${minutesToInputTime(occurrence.startMinutes)}:00`,
      endsAt: `${occurrence.date}T${minutesToInputTime(occurrence.endMinutes)}:00`,
      location: occurrence.location || null,
    })),
  )
  return test && recorderTestArming(test, nowMs) ? [...real, test.occurrence] : real
}

type Reader = Pick<Storage, 'getItem' | 'removeItem'>
type Writer = Pick<Storage, 'setItem' | 'removeItem'>

function isTest(value: unknown): value is RecorderTest {
  if (!value || typeof value !== 'object') return false
  const t = value as Partial<RecorderTest>
  return (
    typeof t.armedAt === 'string' &&
    typeof t.promptAt === 'string' &&
    typeof t.stopAt === 'string' &&
    typeof t.offsetMinutes === 'number' &&
    Number.isFinite(Date.parse(t.armedAt)) &&
    Number.isFinite(Date.parse(t.stopAt)) &&
    !!t.occurrence &&
    t.occurrence.courseCode === RECORDER_TEST_COURSE &&
    typeof t.seen === 'object' &&
    t.seen !== null
  )
}

/**
 * The test this phone has on file, or null. A corrupt value, or a result nobody dismissed within a
 * week, is removed rather than shown; `localStorage` itself can throw, so the read is guarded.
 */
export function readRecorderTest(storage: Reader | null | undefined, nowMs: number): RecorderTest | null {
  try {
    const raw = storage?.getItem(RECORDER_TEST_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isTest(parsed) || nowMs > Date.parse(parsed.armedAt) + RECORDER_TEST_RETENTION_MS) {
      storage?.removeItem(RECORDER_TEST_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/** Persist, or clear with null. A browser that refuses storage keeps the test for this session. */
export function writeRecorderTest(storage: Writer | null | undefined, test: RecorderTest | null): void {
  try {
    if (test) storage?.setItem(RECORDER_TEST_STORAGE_KEY, JSON.stringify(test))
    else storage?.removeItem(RECORDER_TEST_STORAGE_KEY)
  } catch {
    // Nothing to do: the card still works until the screen closes.
  }
}

// ── observing ───────────────────────────────────────────────────────────────
//
// Each returns the SAME object when it learned nothing, so a caller can skip a write and a render.

function withSeen(test: RecorderTest, patch: Partial<RecorderTestSeen>): RecorderTest {
  return { ...test, seen: { ...test.seen, ...patch } }
}

function startedInWindow(test: RecorderTest, startedAt: string | null | undefined): boolean {
  if (!startedAt) return false
  const ms = Date.parse(startedAt)
  return Number.isFinite(ms) && ms >= Date.parse(test.armedAt) - START_TOLERANCE_MS
}

/** The phone read back an alarm for the test at the planned prompt time. */
export function observeSchedule(test: RecorderTest, schedule: NativeSchedule | null, nowMs: number): RecorderTest {
  if (test.seen.armed || !schedule) return test
  const promptMs = Date.parse(test.promptAt)
  const match = schedule.scheduled.find(
    (s) =>
      s.courseCode === RECORDER_TEST_COURSE &&
      Math.abs(Date.parse(s.startsAt) - promptMs) < MINUTE_MS,
  )
  if (!match) return test
  return withSeen(test, {
    armed: {
      startsAt: match.startsAt,
      endsAt: match.endsAt,
      exact: schedule.exactAlarms,
      seenAt: new Date(nowMs).toISOString(),
    },
  })
}

/**
 * The phone's prompt log: how the test's prompt was answered. An OPEN prompt is not an answer and
 * ticks nothing; a later answer (a failed start retried) replaces an earlier one.
 */
export function observePrompts(
  test: RecorderTest,
  prompts: readonly NativeClassPrompt[] | null,
  nowMs: number,
): RecorderTest {
  if (!prompts) return test
  const promptMs = Date.parse(test.promptAt)
  const match = prompts
    .filter(
      (p) =>
        p.courseCode === RECORDER_TEST_COURSE &&
        !!p.startsAt &&
        Math.abs(Date.parse(p.startsAt) - promptMs) < MINUTE_MS,
    )
    .pop()
  if (!match || match.outcome === 'open') return test
  const answeredAt = match.answeredAt ?? null
  const seen = test.seen.prompt
  if (seen && seen.outcome === match.outcome && seen.answeredAt === answeredAt) return test
  return withSeen(test, {
    prompt: {
      outcome: match.outcome,
      answeredAt,
      reason: match.reason ?? null,
      seenAt: new Date(nowMs).toISOString(),
    },
  })
}

/** The service said the SCHEDULE's recording for the test course is running (Start was tapped). */
export function observeStatus(test: RecorderTest, status: NativeRecorderStatus | null, nowMs: number): RecorderTest {
  if (test.seen.started || !status?.recording) return test
  if (status.courseCode !== RECORDER_TEST_COURSE || status.scheduled !== true) return test
  if (!status.startedAt || !startedInWindow(test, status.startedAt)) return test
  return withSeen(test, {
    started: { startedAt: status.startedAt, seenAt: new Date(nowMs).toISOString() },
    ...(status.path ? { path: status.path } : {}),
  })
}

/** The test's file, if the phone's list has it. Alarm-started, the test course, after arming. */
export function findTestRecording(
  test: RecorderTest,
  recordings: readonly NativeRecordingEntry[],
): NativeRecordingEntry | null {
  if (test.seen.path) {
    const byPath = recordings.find((r) => r.path === test.seen.path)
    if (byPath) return byPath
  }
  return (
    recordings.find(
      (r) =>
        r.courseCode === RECORDER_TEST_COURSE && r.scheduled === true && startedInWindow(test, r.startedAt),
    ) ?? null
  )
}

/** The phone's own list: when it started, and — once nothing is writing it — that it stopped. */
export function observeRecordings(
  test: RecorderTest,
  recordings: readonly NativeRecordingEntry[],
  nowMs: number,
): RecorderTest {
  const file = findTestRecording(test, recordings)
  if (!file) return test
  const seenAt = new Date(nowMs).toISOString()
  const patch: Partial<RecorderTestSeen> = {}
  if (test.seen.path !== file.path) patch.path = file.path
  if (!test.seen.started && file.startedAt) patch.started = { startedAt: file.startedAt, seenAt }
  if (!test.seen.stopped && !file.recording) {
    patch.stopped = {
      durationMs: typeof file.durationMs === 'number' ? file.durationMs : null,
      finalized: file.finalized,
      seenAt,
    }
  }
  return Object.keys(patch).length > 0 ? withSeen(test, patch) : test
}

/** The durable queue: the entry for the test's file, and the server's receipt when it has one. */
export function observeQueue(test: RecorderTest, entries: readonly QueueEntry[]): RecorderTest {
  if (!test.seen.path) return test
  const entry = entries.find((e) => e.stageId === `${NATIVE_STAGE_PREFIX}${test.seen.path}`)
  if (!entry) return test
  const patch: Partial<RecorderTestSeen> = {}
  if (!test.seen.queued) patch.queued = { at: entry.createdAt }
  if (!test.seen.uploaded && entry.receiptAt && entry.receiptClientUploadId) {
    patch.uploaded = { at: entry.receiptAt, status: entry.receiptStatus ?? entry.state }
  }
  if (!test.seen.lectureId && entry.lectureId) patch.lectureId = entry.lectureId
  return Object.keys(patch).length > 0 ? withSeen(test, patch) : test
}

/** The server's lecture list: the pipeline's own `hasTranscript`, never the status chip. */
export function observeLectures(
  test: RecorderTest,
  lectures: readonly { id: string; hasTranscript: boolean }[],
  nowMs: number,
): RecorderTest {
  if (test.seen.transcribed || !test.seen.lectureId) return test
  const lecture = lectures.find((l) => l.id === test.seen.lectureId)
  if (!lecture?.hasTranscript) return test
  return withSeen(test, { transcribed: { seenAt: new Date(nowMs).toISOString() } })
}

/**
 * The PHONE'S OWN UPLOADER: when it owns the test's file, its upload state is what "In the upload
 * queue" and "Uploaded" read — the web queue never sees that file (lib/native-recorder.ts →
 * nativeUploaderOwns). `received` is the phone's record of the server's receipt (`received: true`
 * under this recording's own clientUploadId), the same fact the web queue stores as its receipt.
 */
export function observeNativeUpload(
  test: RecorderTest,
  recordings: readonly NativeRecordingEntry[],
  nowMs: number,
): RecorderTest {
  const file = findTestRecording(test, recordings)
  const upload = file?.upload
  if (!file || !upload) return test
  const seenAt = new Date(nowMs).toISOString()
  const patch: Partial<RecorderTestSeen> = {}
  const lastError = upload.lastError ?? null
  const was = test.seen.native
  if (!was || was.state !== upload.state || was.attempts !== upload.attempts || was.lastError !== lastError) {
    patch.native = { state: upload.state, attempts: upload.attempts, lastError, seenAt }
  }
  if (test.seen.path !== file.path) patch.path = file.path
  if (!test.seen.queued) patch.queued = { at: upload.at ?? seenAt, by: 'phone' }
  if (!test.seen.uploaded && upload.state === 'received') {
    patch.uploaded = { at: upload.at ?? seenAt, status: 'received', by: 'phone' }
  }
  if (!test.seen.lectureId && upload.lectureId) patch.lectureId = upload.lectureId
  return Object.keys(patch).length > 0 ? withSeen(test, patch) : test
}

/** True while the phone's uploader holds the test's file and has not delivered it yet. */
export function awaitingPhoneUpload(test: RecorderTest | null): boolean {
  return Boolean(test?.seen.native) && !test?.seen.uploaded
}

// ── what the server is told ─────────────────────────────────────────────────

/** The course a finished recording is uploaded under. The test goes up UNFILED. */
export function uploadCourseFor(courseCode: string | null | undefined): string | null {
  if (!courseCode || courseCode === RECORDER_TEST_COURSE) return null
  return courseCode
}

/** The title it is uploaded with. Only the test is named here; everything else keeps its file name. */
export function uploadTitleFor(courseCode: string | null | undefined): string | null {
  return courseCode === RECORDER_TEST_COURSE ? RECORDER_TEST_TITLE : null
}

// ── the checklist ───────────────────────────────────────────────────────────

export interface ChecklistRow {
  key: 'armed' | 'prompted' | 'started' | 'stopped' | 'queued' | 'uploaded' | 'transcribed'
  label: string
  /** True only when the row's fact was read, never when it is merely due. */
  seen: boolean
  /** The evidence in words, or 'not seen yet'. */
  detail: string
  /** Seen, and what was seen is a failure (an interrupted file, a refused import). */
  problem?: boolean
}

export const NOT_SEEN = 'not seen yet'

function seconds(ms: number): string {
  const s = Math.round(Math.abs(ms) / 1000)
  if (s < 90) return `${s} s`
  return `${Math.round(s / 60)} min`
}

function length(ms: number): string {
  const total = Math.round(ms / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/**
 * The result, row by row, in the words the owner reports back. `refusal` is the queue's own
 * sentence when the file was found but could not be queued.
 */
export function recorderTestChecklist(
  test: RecorderTest,
  extras: { refusal?: string | null; lectureStatus?: string | null } = {},
): ChecklistRow[] {
  const { seen } = test
  const promptMs = Date.parse(test.promptAt)
  const rows: ChecklistRow[] = []

  rows.push({
    key: 'armed',
    label: 'Alarm armed on the phone',
    seen: Boolean(seen.armed),
    detail: seen.armed
      ? `the phone read back ${wallTime(Date.parse(seen.armed.startsAt))} → ${wallTime(Date.parse(seen.armed.endsAt))}` +
        (seen.armed.exact ? '' : ' · inexact, Android may fire it late')
      : NOT_SEEN,
    problem: seen.armed ? !seen.armed.exact : false,
  })

  // The prompt's answer, off the phone's own log. Anything but Start is seen AND a problem: the
  // test did not run, and the row says exactly why.
  let prompted = NOT_SEEN
  let promptProblem = false
  const answer = seen.prompt
  if (answer) {
    const at = answer.answeredAt ? ` at ${wallTime(Date.parse(answer.answeredAt))}` : ''
    if (answer.outcome === 'started') prompted = `Start tapped${at} · the phone’s own record`
    else {
      promptProblem = true
      prompted =
        answer.outcome === 'not-in-class'
          ? `Not in class was tapped${at}, so nothing was recorded`
          : answer.outcome === 'not-answered'
            ? `not answered — the prompt cleared itself${at}`
            : `Start was tapped${at}, but it did not start: ${answer.reason ?? 'no reason given'}`
    }
  }
  rows.push({
    key: 'prompted',
    label: 'Prompt shown, Start tapped',
    seen: answer?.outcome === 'started',
    detail: prompted,
    problem: promptProblem,
  })

  let started = NOT_SEEN
  if (seen.started) {
    const lag = Date.parse(seen.started.startedAt) - promptMs
    const when = wallTime(Date.parse(seen.started.startedAt))
    const relative = Math.abs(lag) < 1000 ? 'on time' : lag > 0 ? `${seconds(lag)} after it was due` : `${seconds(lag)} before it was due`
    started = `at ${when}, ${relative} · the phone’s own start time`
  }
  rows.push({ key: 'started', label: 'Recording started', seen: Boolean(seen.started), detail: started })

  let stopped = NOT_SEEN
  let stoppedProblem = false
  if (seen.stopped) {
    if (!seen.stopped.finalized) {
      stopped = 'interrupted — the file was never closed, so it cannot be uploaded'
      stoppedProblem = true
    } else if (seen.stopped.durationMs !== null) {
      const at = seen.started ? ` at ${wallTime(Date.parse(seen.started.startedAt) + seen.stopped.durationMs)}` : ''
      stopped = `after ${length(seen.stopped.durationMs)}${at} · the length the phone measured`
    } else {
      stopped = 'the phone finished the file but reported no length'
    }
  }
  rows.push({ key: 'stopped', label: 'Recording stopped', seen: Boolean(seen.stopped), detail: stopped, problem: stoppedProblem })

  let queued = NOT_SEEN
  let queuedProblem = false
  if (seen.queued?.by === 'phone') {
    queued = `the phone’s own uploader took it at ${wallTime(Date.parse(seen.queued.at))}`
  } else if (seen.queued) queued = `since ${wallTime(Date.parse(seen.queued.at))}`
  else if (extras.refusal) {
    queued = `not queued — ${extras.refusal}`
    queuedProblem = true
  }
  rows.push({ key: 'queued', label: 'In the upload queue', seen: Boolean(seen.queued), detail: queued, problem: queuedProblem })

  // The phone's uploader's own words while it has not delivered: a failure is seen AND a problem;
  // a retry is only "not yet", with the reason the last attempt did not land.
  let uploaded = NOT_SEEN
  let uploadedProblem = false
  if (seen.uploaded) {
    uploaded =
      `the server confirmed it at ${wallTime(Date.parse(seen.uploaded.at))}` +
      (seen.uploaded.by === 'phone' ? ' · sent by the phone, LifeOS closed or not' : '')
  } else if (seen.native?.state === 'failed') {
    uploaded = `not uploaded — ${seen.native.lastError ?? 'the phone gave no reason'}`
    uploadedProblem = true
  } else if (seen.native?.state === 'uploading') {
    uploaded = `${NOT_SEEN} · the phone is sending it now`
  } else if (seen.native?.lastError) {
    uploaded = `${NOT_SEEN} · the phone will try again (${seen.native.lastError})`
  }
  rows.push({ key: 'uploaded', label: 'Uploaded', seen: Boolean(seen.uploaded), detail: uploaded, problem: uploadedProblem })

  let transcribed = NOT_SEEN
  if (seen.transcribed) transcribed = `seen at ${wallTime(Date.parse(seen.transcribed.seenAt))}`
  else if (extras.lectureStatus) transcribed = `${NOT_SEEN} · the server says “${extras.lectureStatus}”`
  rows.push({ key: 'transcribed', label: 'Transcribed', seen: Boolean(seen.transcribed), detail: transcribed })

  return rows
}
